import {ReactNode, useState} from 'react';

// Stacked completions-per-bucket chart with an optional dashed goal line.
// Follows the same visual language as the TeamActivity charts (whose
// ChartStyles component provides the shared animation/tooltip CSS).

interface TooltipState {
  leftPct: number;
  content: ReactNode;
}

export interface VettingSeriesInput {
  key: string;
  label: string;
  color: string;
  values: number[];
}

export const GoalStackedChart = ({
  bucketLabels,
  series,
  goal = 0,
  goalLabel = 'goal',
}: {
  bucketLabels: string[];
  series: VettingSeriesInput[];
  goal?: number;
  goalLabel?: string;
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const [tt, setTt] = useState<TooltipState | null>(null);

  const W = 720;
  const H = 240;
  const padL = 32;
  // The goal label ("goal 900") is drawn left-anchored at W - padR + 5, so padR must grow
  // with the label's rendered width or long numbers get clipped by the SVG viewBox edge.
  const goalLabelWidth = goal > 0 ? goalLabel.length * 6.6 : 0;
  const padR = goal > 0 ? Math.max(48, goalLabelWidth + 13) : 8;
  const padT = 10;
  const padB = 26;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const baseY = padT + plotH;

  const n = bucketLabels.length;
  const totals = bucketLabels.map((_, i) => series.reduce((a, s) => a + (s.values[i] || 0), 0));
  const maxValue = Math.max(...totals, goal, 1);
  const stepY = maxValue > 24 ? Math.ceil(maxValue / 40) * 10 : 5;
  const yMax = Math.max(stepY, Math.ceil(maxValue / stepY) * stepY);
  const yScale = (v: number) => (v / yMax) * plotH;
  const band = plotW / n;
  const barW = Math.min(26, band * 0.62);
  const labelEvery = n > 16 ? Math.ceil(n / 15) : 1;

  const gridLines: number[] = [];
  for (let v = 0; v <= yMax; v += stepY) gridLines.push(v);

  const showBucket = (i: number) => {
    setHover(i);
    setTt({
      leftPct: ((padL + band * i + band / 2) / W) * 100,
      content: (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            <span>{bucketLabels[i]}</span>
            <span style={{color: '#6C6579', fontWeight: 600}}>{totals[i]} vetted</span>
          </div>
          {series
            .filter((s) => (s.values[i] || 0) > 0)
            .map((s) => (
              <div
                key={s.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '1.5px 0',
                }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: 2.5,
                    background: s.color,
                    flex: 'none',
                  }}
                />
                <span style={{color: '#6C6579'}}>{s.label}</span>
                <span style={{marginLeft: 'auto', fontWeight: 700}}>{s.values[i]}</span>
              </div>
            ))}
          {totals[i] === 0 && <div style={{color: '#9A93A8'}}>No completions</div>}
          {goal > 0 && (
            <div
              style={{
                marginTop: 6,
                paddingTop: 5,
                borderTop: '1px solid #ECE7F1',
                color: totals[i] >= goal ? '#2F9E44' : '#9A93A8',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {totals[i] >= goal ? `Goal met (${goal})` : `${goal - totals[i]} below goal (${goal})`}
            </div>
          )}
        </>
      ),
    });
  };

  const goalY = baseY - yScale(goal);

  return (
    <div style={{position: 'relative'}}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{display: 'block', width: '100%', height: 'auto'}}
        role="img"
        aria-label="Stacked bar chart of completed vettings per period by team member"
      >
        {gridLines.map((v) => {
          const y = baseY - yScale(v);
          return (
            <g key={`grid-${v}`}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#ECE7F1" strokeWidth="1" opacity={v === 0 ? 1 : 0.6} />
              <text x={padL - 7} y={y + 3.5} textAnchor="end" fontSize="11" fill="#9A93A8">
                {v}
              </text>
            </g>
          );
        })}
        {bucketLabels.map((label, i) => {
          const cx = padL + band * i + band / 2;
          let acc = 0;
          const segs = series.filter((s) => (s.values[i] || 0) > 0);
          const active = hover === i;
          const dimmed = hover !== null && hover !== i;
          return (
            <g key={`bucket-${label}-${i}`}>
              <rect x={cx - band / 2 + 1} y={padT} width={band - 2} height={plotH} rx={4} fill="#AE3EC9" opacity={active ? 0.07 : 0} />
              {segs.map((s, si) => {
                const val = s.values[i];
                const hgt = yScale(val);
                const y = baseY - yScale(acc) - hgt;
                acc += val;
                const isTop = si === segs.length - 1;
                return (
                  <rect
                    key={s.key}
                    className="ta-bar"
                    style={{animationDelay: `${i * 22}ms`}}
                    x={cx - barW / 2}
                    y={y}
                    width={barW}
                    height={Math.max(0.5, hgt - (isTop ? 0 : 2))}
                    rx={isTop ? Math.min(4, barW / 2) : 0}
                    fill={s.color}
                    opacity={dimmed ? 0.4 : 1}
                  />
                );
              })}
              {i % labelEvery === 0 && (
                <text
                  x={cx}
                  y={baseY + 16}
                  textAnchor="middle"
                  fontSize="11"
                  fill={active ? '#201A2C' : '#9A93A8'}
                  fontWeight={active ? 700 : 400}
                >
                  {label}
                </text>
              )}
              <rect
                x={cx - band / 2}
                y={padT}
                width={band}
                height={plotH + padB}
                fill="transparent"
                onMouseEnter={() => showBucket(i)}
                onMouseLeave={() => {
                  setHover(null);
                  setTt(null);
                }}
              />
            </g>
          );
        })}
        {goal > 0 && goal <= yMax && (
          <g>
            <line x1={padL} y1={goalY} x2={W - padR} y2={goalY} stroke="#6C6579" strokeWidth="1.5" strokeDasharray="5 4" />
            <text x={W - padR + 5} y={goalY + 3.5} fontSize="10.5" fontWeight="700" fill="#6C6579">
              {goalLabel}
            </text>
          </g>
        )}
      </svg>
      <div
        className={`ta-tt${tt ? ' on' : ''}`}
        style={{
          left: `${Math.min(86, Math.max(13, tt?.leftPct ?? 50))}%`,
          top: 16,
        }}
        role="status"
      >
        {tt?.content}
      </div>
    </div>
  );
};

export const GoalProgress = ({value, goal, color = '#AE3EC9'}: {value: number; goal: number; color?: string}) => {
  const pct = goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
  const met = goal > 0 && value >= goal;
  return (
    <div>
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: '#F1EDF6',
          overflow: 'hidden',
        }}
      >
        <div
          className="ta-mixseg"
          style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 4,
            background: met ? '#2F9E44' : color,
          }}
        />
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 11,
          fontWeight: 600,
          color: met ? '#2F9E44' : '#6C6579',
        }}
      >
        {goal > 0 ? `${value} of ${goal}${met ? ' · goal met' : ''}` : `${value}`}
      </div>
    </div>
  );
};
